import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { OwnerTypes } from '@/api/fund/types';
import { _getPortfolio, PortfolioPosition } from '@/api/fund-views/queries';
import { getPortfolioPerformanceOptions } from '@/api/portfolio/queries';
import { _getOwnerships } from '@/api/user-dashboard/queries';
import ArrowDownIcon from '@/assets/svg/ui-elements/arrow-down.svg';
import ArrowUpIcon from '@/assets/svg/ui-elements/arrow-up.svg';
import { Typography } from '@/design-system';
import { FeatureFlags, useFeatureFlag } from '@/global-hooks/use-feature-flag-enabled';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { useSelectedUserConfigSlice } from '@/stores/selectors';

import { ChartPeriod, ChartPeriodType } from '../../common';
import AssetTable from '../assets-table';
import CryptoAssets from '../crypto-assets/crypto-assets';
import { CryptoPerformanceSelection } from '../crypto-assets/types';
import OwnershipInEntitiesTable, { OwnershipInEntitiesRow } from '../ownership-in-entities-table';
import { PendingAssetsSection } from '../pending-assets/pending-assets';
import { PortfolioPerformance } from '../portfolio-performance/portfolio-performance';
import {
  getMobilePortfolioSectionKeys,
  MobilePortfolioSectionKey,
  MobilePortfolioSectionTabs,
} from './mobile-portfolio-section-tabs';
import {
  DesktopSections,
  MobileSections,
  PortfolioSection,
  SectionActionButton,
  SectionActions,
  SectionBody,
  SectionHeader,
} from './my-assets.styled';

type PerformanceSelection =
  | { kind: 'traditional'; id: string; name: string; position: PortfolioPosition }
  | { kind: 'crypto'; id: string; name: string }
  | null;

const MyAssets = () => {
  const isMobile = useIsMobile();
  const isCryptoWalletEnabled = useFeatureFlag(FeatureFlags.CRYPTO_WALLET) === true;
  const isInvestingFlowEnabled = useFeatureFlag(FeatureFlags.INVESTING_FLOW) === true;
  const { selectedUserRole, selectedEntityId } = useSelectedUserConfigSlice();
  const isPersonalUser = selectedUserRole === 'user';
  const showsCryptoWallet = isPersonalUser && isCryptoWalletEnabled;

  const { data } = useQuery(_getPortfolio());
  const { data: ownershipsData } = useQuery(_getOwnerships());
  const [activePeriod, setActivePeriod] = useState<ChartPeriodType>(ChartPeriod['1w']);
  const [performanceSelection, setPerformanceSelection] = useState<PerformanceSelection>(null);
  const [mobileSection, setMobileSection] = useState<MobilePortfolioSectionKey>('assets');

  const { data: performanceData, isPending: isPerformancePending } = useQuery(
    getPortfolioPerformanceOptions({
      period: activePeriod,
      position: performanceSelection?.id,
      ownerType: selectedUserRole === 'entity' ? OwnerTypes.ENTITY : OwnerTypes.USER,
      ownerId: selectedUserRole === 'entity' ? (selectedEntityId ?? undefined) : undefined,
    }),
  );

  const handlePeriod = useCallback((period: ChartPeriodType) => setActivePeriod(period), []);
  const handleTraditionalSelection = useCallback((position: PortfolioPosition | null) => {
    setPerformanceSelection(
      position
        ? { kind: 'traditional', id: position.id, name: position.asset.name, position }
        : null,
    );
  }, []);
  const handleCryptoSelection = useCallback((selection: CryptoPerformanceSelection | null) => {
    setPerformanceSelection(
      selection ? { kind: 'crypto', id: selection.id, name: selection.name } : null,
    );
  }, []);
  const activeTraditionalPosition =
    performanceSelection?.kind === 'traditional' ? performanceSelection.position : null;

  const ownershipRows: OwnershipInEntitiesRow[] =
    ownershipsData?.entities?.flatMap((entity) =>
      (entity.ownerships ?? []).map((o, idx) => ({
        id: `${entity.entityId}-${o.class}-${idx}`,
        entityName: entity.entityName,
        units: o.units,
        percentOfTotal: o.percentOfTotal,
        shareClass: o.class,
      })),
    ) ?? [];

  const mobileSectionKeys = getMobilePortfolioSectionKeys({
    isCryptoWalletEnabled: showsCryptoWallet,
    isInvestingFlowEnabled,
    isPersonalUser,
  });

  const assetSection = (
    <PortfolioSection>
      <SectionHeader>
        <Typography as="h3" variant="h5" color="text.primary">
          My Assets & Liabilities
        </Typography>
        {showsCryptoWallet && (
          <SectionActions>
            <SectionActionButton size="md" variant="subtle" color="primary" disabled>
              <ArrowUpIcon /> Send
            </SectionActionButton>
            <SectionActionButton size="md" variant="subtle" color="primary" disabled>
              <ArrowDownIcon /> Receive
            </SectionActionButton>
          </SectionActions>
        )}
      </SectionHeader>
      <SectionBody>
        <AssetTable
          data={data?.positions ?? []}
          activeAsset={activeTraditionalPosition}
          setActiveAsset={handleTraditionalSelection}
        />
      </SectionBody>
    </PortfolioSection>
  );

  const pendingSection = <PendingAssetsSection />;
  const cryptoSection = (
    <CryptoAssets
      activePositionId={
        performanceSelection?.kind === 'crypto' ? performanceSelection.id : undefined
      }
      onSelectPosition={handleCryptoSelection}
    />
  );
  const ownershipSection = (
    <PortfolioSection>
      <Typography as="h3" variant="h5" color="text.primary">
        Ownership in Entities
      </Typography>
      <SectionBody>
        <OwnershipInEntitiesTable rows={ownershipRows} />
      </SectionBody>
    </PortfolioSection>
  );

  return (
    <>
      <PortfolioPerformance
        data={performanceData}
        isLoading={isPerformancePending}
        activeAssetName={performanceSelection?.name}
        activePeriod={activePeriod}
        onPeriodChange={handlePeriod}
      />

      {isMobile ? (
        <MobileSections role="region" aria-label="Portfolio sections">
          <MobilePortfolioSectionTabs
            sections={mobileSectionKeys.map((key) => ({
              value: key,
              label: {
                assets: 'My Assets & Liabilities',
                pending: 'Pending Assets',
                crypto: 'Crypto Assets',
                ownership: 'Ownership in Entities',
              }[key],
              content: {
                assets: assetSection,
                pending: pendingSection,
                crypto: cryptoSection,
                ownership: ownershipSection,
              }[key],
            }))}
            value={mobileSection}
            onValueChange={setMobileSection}
          />
        </MobileSections>
      ) : (
        <DesktopSections>
          {assetSection}
          {showsCryptoWallet && cryptoSection}
          {isPersonalUser && ownershipSection}
          {isInvestingFlowEnabled && pendingSection}
        </DesktopSections>
      )}
    </>
  );
};

export default MyAssets;
